import { useI18n, I18nMessage, CommonStrings } from '@cvsdigital_web/react-cmk-i18n';
import { translations as I18N_STRING_MAP } from '@/lib/i18n';
import { ElementType } from 'react';

export type TranslationKey = keyof typeof I18N_STRING_MAP;

/**
 * useTranslation hook to provide translation functionality
 * By default useI18n doesn't include English fallback, instead showing the ID, which is not ideal for most use cases.
 */
export const useTranslation = (textKey: TranslationKey) => {
  const result = useI18n(textKey.toString());

  return result === textKey ? I18N_STRING_MAP[textKey] : result;
};

interface TranslationProps {
  translate?: TranslationKey;
  tokens?: CommonStrings | undefined;
  className?: string;
  isHtml?: boolean;
  children?: string | null;
  Component?: ElementType;
  trackingId?: string;
  componentProps?: {
    [key: string]: unknown;
  };
}

function interpolateString(template: string, data: Record<string, unknown> = {}): string {
  // Regular expression to find all occurrences of ${...}
  const regex = /\$\{(.*?)}/g;
  return template.replace(regex, (match, key: string): string => {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      return String(data[key] ?? '');
    }
    return match;
  });
}

/**
 * Translation hook to provide translation component
 * By default I18nMessage doesn't include any text while loading, instead showing the empty space, which is not ideal for our use cases.
 */
export const Translation = ({ translate, ...restOfProps }: TranslationProps) => {
  const translationKey = translate?.toString();
  const result = useI18n(translationKey || '');

  if (result === translationKey) {
    return <span>{interpolateString(I18N_STRING_MAP[translationKey as TranslationKey], restOfProps.tokens as Record<string, unknown>)}</span>;
  }

  return <I18nMessage translate={translationKey} {...restOfProps} />;
};
